import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axiosInstance from './axiosInstance';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  FormControl,
  InputLabel,
  MenuItem,
  InputAdornment,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { ArrowDownward, ArrowUpward, Search } from '@mui/icons-material';
import './ExportGridView.css';

const userColumnWidths = {
  full_name: '13%',
  job_title: '16%',
  email: '20%',
  payroll_number: '9%',
  employment_start: '9%',
  status: '7%',
  labour: '6%',
  location: '12%',
  actions: '8%',
};

const statusOptions = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'all', label: 'All Users' },
];

const labourFilterOptions = [
  { value: 'all', label: 'All Users' },
  { value: 'labour', label: 'Labour' },
  { value: 'non_labour', label: 'Non-Labour' },
];

const formatJoiningDate = (value) => {
  const text = String(value || '').trim();
  if (!text) return '-';

  const isoDateMatch = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoDateMatch) {
    return `${isoDateMatch[3]}/${isoDateMatch[2]}/${isoDateMatch[1]}`;
  }

  const date = new Date(text);
  if (Number.isNaN(date.getTime())) return '-';
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const ManageUsersPage = () => {
  const [users, setUsers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [jobTitles, setJobTitles] = useState([]);
  const [statusFilter, setStatusFilter] = useState('active');
  const [locationFilter, setLocationFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');
  const [labourFilter, setLabourFilter] = useState('all');
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortKey, setSortKey] = useState('full_name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [page, setPage] = useState(0);
  const rowsPerPage = 10;
  const [pageInput, setPageInput] = useState('1');
  const [totalUsers, setTotalUsers] = useState(0);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [syncingUsers, setSyncingUsers] = useState(false);
  const [syncMessage, setSyncMessage] = useState('Sync BrightHR users');
  const [savingLocations, setSavingLocations] = useState({});
  const [draftLocations, setDraftLocations] = useState({});
  const [originalLocations, setOriginalLocations] = useState({});
  const hasAutoSyncedRef = useRef(false);
  const latestUsersRequestRef = useRef(0);
  const previousSearchQueryRef = useRef('');

  const sortedLocations = useMemo(
    () => [...locations].sort((left, right) => {
      const leftName = (left?.name || left?.location_id || '').toString().toLowerCase();
      const rightName = (right?.name || right?.location_id || '').toString().toLowerCase();
      return leftName.localeCompare(rightName);
    }),
    [locations]
  );

  const sortedJobTitles = useMemo(
    () => [...jobTitles].sort((left, right) => (left?.label || '').localeCompare(right?.label || '')),
    [jobTitles],
  );

  const showToast = useCallback((message, severity = 'success') => {
    if (typeof window === 'undefined') return;
    window.dispatchEvent(
      new CustomEvent('hv-toast', {
        detail: { message, severity },
      })
    );
  }, []);

  const loadLocations = useCallback(async () => {
    setLoadingLocations(true);
    try {
      const [locationsResponse, jobTitlesResponse] = await Promise.all([
        axiosInstance.get('/api/v1/location'),
        axiosInstance.get('/api/v1/manage/job-titles', { params: { page: 1, limit: 1000, sort_key: 'job_title', sort_direction: 'asc' } }),
      ]);
      const payload = locationsResponse?.data?.data ?? locationsResponse?.data ?? [];
      const jobTitlePayload = jobTitlesResponse?.data?.data ?? [];
      setLocations(Array.isArray(payload) ? payload : []);
      setJobTitles(
        (Array.isArray(jobTitlePayload) ? jobTitlePayload : [])
          .map((entry) => ({
            value: entry?.normalized_job_title || '',
            label: entry?.job_title || entry?.normalized_job_title || '',
          }))
          .filter((entry) => entry.value && entry.label),
      );
    } catch (error) {
      showToast(error?.response?.data?.error || error?.message || 'Failed to load filter options.', 'error');
    } finally {
      setLoadingLocations(false);
    }
  }, [showToast]);

  const loadUsers = useCallback(async (
    pageIndex = page,
    nextStatusFilter = statusFilter,
    nextLocationFilter = locationFilter,
    nextRoleFilter = roleFilter,
    nextLabourFilter = labourFilter,
    nextSortKey = sortKey,
    nextSortDirection = sortDirection,
    nextSearchQuery = searchQuery,
  ) => {
    const requestId = ++latestUsersRequestRef.current;
    setLoadingUsers(true);
    try {
      const params = {
        page: pageIndex + 1,
        limit: rowsPerPage,
        sort_key: nextSortKey,
        sort_direction: nextSortDirection,
      };
      if (nextStatusFilter !== 'all') {
        params.status = nextStatusFilter;
      }
      if (nextLocationFilter === 'unassigned') {
        params.location_id = '__unassigned__';
      } else if (nextLocationFilter !== 'all') {
        params.location_id = nextLocationFilter;
      }
      if (nextRoleFilter !== 'all') {
        params.job_title = nextRoleFilter;
      }
      if (nextLabourFilter !== 'all') {
        params.labour = nextLabourFilter;
      }
      if (nextSearchQuery?.trim()) {
        params.search = nextSearchQuery.trim();
      }
      const response = await axiosInstance.get('/api/v1/manage/users', { params });
      if (requestId !== latestUsersRequestRef.current) return;
      const payload = response?.data ?? {};
      const nextUsers = Array.isArray(payload?.data) ? payload.data : Array.isArray(payload) ? payload : [];
      setUsers(nextUsers);
      setTotalUsers(Number(payload?.total ?? nextUsers.length ?? 0));
      setDraftLocations(
        nextUsers.reduce((accumulator, user) => {
          accumulator[user.bright_hr_employee_id] = user.location_id || '';
          return accumulator;
        }, {})
      );
      setOriginalLocations(
        nextUsers.reduce((accumulator, user) => {
          accumulator[user.bright_hr_employee_id] = user.location_id || '';
          return accumulator;
        }, {})
      );
    } catch (error) {
      if (requestId !== latestUsersRequestRef.current) return;
      showToast(error?.response?.data?.error || error?.message || 'Failed to load BrightHR users.', 'error');
    } finally {
      if (requestId !== latestUsersRequestRef.current) return;
      setLoadingUsers(false);
    }
  }, [labourFilter, locationFilter, page, roleFilter, rowsPerPage, searchQuery, showToast, sortDirection, sortKey, statusFilter]);

  useEffect(() => {
    loadLocations();
  }, [loadLocations]);

  useEffect(() => {
    if (previousSearchQueryRef.current !== searchQuery) {
      previousSearchQueryRef.current = searchQuery;
      if (page !== 0) {
        setPage(0);
        return;
      }
    }

    loadUsers(page, statusFilter, locationFilter, roleFilter, labourFilter, sortKey, sortDirection, searchQuery);
  }, [labourFilter, loadUsers, page, roleFilter, statusFilter, locationFilter, sortKey, sortDirection, searchQuery]);

  useEffect(() => {
    setPageInput(String(page + 1));
  }, [page, totalUsers]);

  const handleSyncUsers = useCallback(async (force = false, showSuccessToast = true) => {
    setSyncingUsers(true);
    try {
      const response = await axiosInstance.post('/api/v1/manage/users', { force });
      const payload = response?.data?.data ?? response?.data ?? {};
      const successMessage = `${payload?.message || 'BrightHR users synced successfully.'} ${payload?.total_records || 0} users updated.`;
      setSyncMessage(successMessage);
      if (showSuccessToast) {
        showToast(successMessage, 'success');
      }
      await loadUsers(page, statusFilter, locationFilter, roleFilter, labourFilter, sortKey, sortDirection, searchQuery);
    } catch (error) {
      const failureMessage = error?.response?.data?.error || error?.message || 'Failed to sync BrightHR users.';
      setSyncMessage(failureMessage);
      showToast(failureMessage, 'error');
    } finally {
      setSyncingUsers(false);
    }
  }, [labourFilter, loadUsers, locationFilter, page, roleFilter, searchQuery, showToast, sortDirection, sortKey, statusFilter]);

  useEffect(() => {
    if (hasAutoSyncedRef.current) return;
    hasAutoSyncedRef.current = true;
    handleSyncUsers(false, false);
  }, [handleSyncUsers]);

  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
    setPage(0);
  };

  const handleLocationFilterChange = (event) => {
    setLocationFilter(event.target.value);
    setPage(0);
  };

  const handleRoleFilterChange = (event) => {
    setRoleFilter(event.target.value);
    setPage(0);
  };

  const handleLabourFilterChange = (event) => {
    setLabourFilter(event.target.value);
    setPage(0);
  };

  const applySearch = useCallback(() => {
    setPage(0);
    setSearchQuery(searchInput.trim());
  }, [searchInput]);

  const handleSearchKeyDown = (event) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    applySearch();
  };

  const handleSearchChange = (event) => {
    setSearchInput(event.target.value);
  };

  const hasActiveFilters = Boolean(searchInput || searchQuery || statusFilter !== 'active' || locationFilter !== 'all' || roleFilter !== 'all' || labourFilter !== 'all');

  const clearFilters = () => {
    setSearchInput('');
    setSearchQuery('');
    setStatusFilter('active');
    setLocationFilter('all');
    setRoleFilter('all');
    setLabourFilter('all');
    setPage(0);
  };

  const handleSortChange = (columnKey) => {
    const nextDirection = sortKey === columnKey ? (sortDirection === 'asc' ? 'desc' : 'asc') : 'asc';
    setSortKey(columnKey);
    setSortDirection(nextDirection);
    setPage(0);
    loadUsers(0, statusFilter, locationFilter, roleFilter, labourFilter, columnKey, nextDirection, searchQuery);
  };

  const handlePageChange = (_event, newPage) => {
    if (newPage === page) return;
    setPage(newPage);
  };

  const handlePageInputChange = (event) => {
    const numericValue = event.target.value.replace(/[^0-9]/g, '');
    setPageInput(numericValue);
  };

  const handlePageInputKeyDown = (event) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    if (!totalUsers) {
      setPageInput('1');
      return;
    }
    const desiredPage = Number(pageInput);
    if (!Number.isFinite(desiredPage) || desiredPage < 1) {
      setPageInput(String(page + 1));
      return;
    }
    const totalPages = Math.max(Math.ceil(totalUsers / rowsPerPage), 1);
    const clampedPage = Math.min(Math.max(Math.floor(desiredPage), 1), totalPages);
    if (clampedPage !== page + 1) {
      setPage(clampedPage - 1);
    } else {
      setPageInput(String(clampedPage));
    }
  };

  const handleLocationChange = (brightHrEmployeeId, locationId) => {
    setDraftLocations((current) => ({
      ...current,
      [brightHrEmployeeId]: locationId,
    }));
  };

  const handleSaveLocation = async (user) => {
    const brightHrEmployeeId = user?.bright_hr_employee_id;
    const locationId = draftLocations[brightHrEmployeeId] ?? '';

    if (!brightHrEmployeeId) {
      return;
    }

    setSavingLocations((current) => ({ ...current, [brightHrEmployeeId]: true }));

    try {
      await axiosInstance.put(`/api/v1/manage/users/${brightHrEmployeeId}`, {
        location_id: locationId,
      });
      showToast(`Updated location for ${user?.full_name || brightHrEmployeeId}.`, 'success');
      await loadUsers();
    } catch (error) {
      showToast(error?.response?.data?.error || error?.message || 'Failed to update the user location.', 'error');
    } finally {
      setSavingLocations((current) => ({ ...current, [brightHrEmployeeId]: false }));
    }
  };

  const locationOptions = useMemo(() => [{ location_id: '', name: 'Unassigned' }, ...sortedLocations], [sortedLocations]);
  const renderSortIcon = (columnKey) => {
    if (sortKey !== columnKey) {
      return null;
    }

    return sortDirection === 'asc' ? <ArrowUpward className="sortIcon" /> : <ArrowDownward className="sortIcon" />;
  };

  const hasLocationChanged = (user) => {
    const brightHrEmployeeId = user?.bright_hr_employee_id;
    if (!brightHrEmployeeId) {
      return false;
    }
    const initialLocationId = originalLocations[brightHrEmployeeId] ?? user?.location_id ?? '';
    const currentLocationId = draftLocations[brightHrEmployeeId] ?? user?.location_id ?? '';
    return currentLocationId !== initialLocationId;
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Paper sx={{ p: { xs: 2, md: 3 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap', textAlign: 'left' }}>
          <Box>
            <Typography variant="h4" component="h1" sx={{ mb: 0.5, fontWeight: 500 }}>
              BrightHR Users
            </Typography>
          </Box>

          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" justifyContent="flex-end">
          <TextField
            value={searchInput}
            onChange={handleSearchChange}
            label="Search Users"
            size="small"
            placeholder="Name, email, payroll, job title"
            sx={{ minWidth: 320 }}
            onKeyDown={handleSearchKeyDown}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton aria-label="search users" edge="end" onClick={applySearch}>
                    <Search />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel id="bright-hr-user-status-filter-label">Status</InputLabel>
            <Select
              labelId="bright-hr-user-status-filter-label"
              value={statusFilter}
              label="Status"
              onChange={handleStatusFilterChange}
            >
              {statusOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel id="bright-hr-user-location-filter-label">Location</InputLabel>
            <Select
              labelId="bright-hr-user-location-filter-label"
              value={locationFilter}
              label="Location"
              onChange={handleLocationFilterChange}
            >
              <MenuItem value="all">All Locations</MenuItem>
              <MenuItem value="unassigned">Unassigned</MenuItem>
              {sortedLocations.map((location) => (
                <MenuItem key={location.location_id} value={location.location_id}>
                  {location.name || location.location_id}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 210 }}>
            <InputLabel id="bright-hr-user-role-filter-label">Role</InputLabel>
            <Select
              labelId="bright-hr-user-role-filter-label"
              value={roleFilter}
              label="Role"
              onChange={handleRoleFilterChange}
              disabled={loadingLocations}
            >
              <MenuItem value="all">All Roles</MenuItem>
              {sortedJobTitles.map((jobTitle) => (
                <MenuItem key={jobTitle.value} value={jobTitle.value}>
                  {jobTitle.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 170 }}>
            <InputLabel id="bright-hr-user-labour-filter-label">Labour</InputLabel>
            <Select
              labelId="bright-hr-user-labour-filter-label"
              value={labourFilter}
              label="Labour"
              onChange={handleLabourFilterChange}
            >
              {labourFilterOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            variant="outlined"
            onClick={clearFilters}
            disabled={!hasActiveFilters}
            sx={{ minWidth: 130, whiteSpace: 'nowrap' }}
          >
            Clear Filters
          </Button>

          <Tooltip title={syncingUsers ? 'Syncing BrightHR users…' : syncMessage} arrow>
            <span>
              <Button variant="contained" onClick={() => handleSyncUsers(true, true)} disabled={syncingUsers} sx={{ minWidth: 210 }}>
                {syncingUsers ? <CircularProgress size={20} sx={{ color: 'white', mr: 1 }} /> : null}
                {syncingUsers ? 'Syncing BrightHR Users' : 'Sync BrightHR Users'}
              </Button>
            </span>
          </Tooltip>
          </Stack>
        </Box>

        <TableContainer
          sx={{
            position: 'relative',
            maxHeight: '68vh',
            minHeight: loadingUsers && !users.length ? 200 : 'auto',
            mt: 2,
            overflowX: 'auto',
            overflowY: 'auto',
          }}
        >
          {loadingUsers && users.length > 0 && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1.5,
                bgcolor: 'rgba(255, 255, 255, 0.82)',
                backdropFilter: 'blur(1px)',
                pointerEvents: 'none',
                zIndex: 3,
              }}
            >
              <CircularProgress size={48} thickness={4} />
              <Typography variant="body2" color="text.secondary">
                Loading next page...
              </Typography>
            </Box>
          )}
          <Table
            stickyHeader
            size="small"
            sx={{
              tableLayout: 'fixed',
              '& .MuiTableCell-sizeSmall': {
                padding: '8px 16px',
                height: 44,
                boxSizing: 'border-box',
              },
              '& .MuiTableHead .MuiTableCell-sizeSmall': {
                padding: '12px 16px',
                height: 48,
              },
              '& .MuiInputBase-root.MuiInputBase-sizeSmall': {
                minHeight: 28,
              },
              '& .MuiSelect-select.MuiSelect-select': {
                paddingTop: '4px',
                paddingBottom: '4px',
              },
              '& .MuiButton-sizeSmall': {
                minHeight: 28,
                paddingTop: 0,
                paddingBottom: 0,
              },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell className={`sortableHeader ${sortKey === 'full_name' ? 'sorted' : ''}`} sx={{ width: userColumnWidths.full_name, fontWeight: 600, whiteSpace: 'nowrap' }}>
                  <div className="headerContent" onClick={() => handleSortChange('full_name')} role="button" tabIndex={0}>
                    <strong>Full Name</strong>
                    {renderSortIcon('full_name')}
                  </div>
                </TableCell>
                <TableCell className={`sortableHeader ${sortKey === 'job_title' ? 'sorted' : ''}`} sx={{ width: userColumnWidths.job_title, fontWeight: 600, whiteSpace: 'nowrap' }}>
                  <div className="headerContent" onClick={() => handleSortChange('job_title')} role="button" tabIndex={0}>
                    <strong>Job Title</strong>
                    {renderSortIcon('job_title')}
                  </div>
                </TableCell>
                <TableCell className={`sortableHeader ${sortKey === 'email' ? 'sorted' : ''}`} sx={{ width: userColumnWidths.email, fontWeight: 600, whiteSpace: 'nowrap' }}>
                  <div className="headerContent" onClick={() => handleSortChange('email')} role="button" tabIndex={0}>
                    <strong>Email</strong>
                    {renderSortIcon('email')}
                  </div>
                </TableCell>
                <TableCell className={`sortableHeader ${sortKey === 'payroll_number' ? 'sorted' : ''}`} sx={{ width: userColumnWidths.payroll_number, fontWeight: 600, whiteSpace: 'nowrap' }}>
                  <div className="headerContent" onClick={() => handleSortChange('payroll_number')} role="button" tabIndex={0}>
                    <strong>Payroll No.</strong>
                    {renderSortIcon('payroll_number')}
                  </div>
                </TableCell>
                <TableCell className={`sortableHeader ${sortKey === 'employment_start' ? 'sorted' : ''}`} sx={{ width: userColumnWidths.employment_start, fontWeight: 600, whiteSpace: 'nowrap' }}>
                  <div className="headerContent" onClick={() => handleSortChange('employment_start')} role="button" tabIndex={0}>
                    <strong>Joining Date</strong>
                    {renderSortIcon('employment_start')}
                  </div>
                </TableCell>
                <TableCell className={`sortableHeader ${sortKey === 'status' ? 'sorted' : ''}`} sx={{ width: userColumnWidths.status, fontWeight: 600, whiteSpace: 'nowrap' }}>
                  <div className="headerContent" onClick={() => handleSortChange('status')} role="button" tabIndex={0}>
                    <strong>Status</strong>
                    {renderSortIcon('status')}
                  </div>
                </TableCell>
                <TableCell sx={{ width: userColumnWidths.labour, fontWeight: 600, whiteSpace: 'nowrap' }}>
                  <strong>Labour</strong>
                </TableCell>
                <TableCell className={`sortableHeader ${sortKey === 'location' ? 'sorted' : ''}`} sx={{ width: userColumnWidths.location, maxWidth: userColumnWidths.location, fontWeight: 600, whiteSpace: 'nowrap' }}>
                  <div className="headerContent" onClick={() => handleSortChange('location')} role="button" tabIndex={0}>
                    <strong>Location</strong>
                    {renderSortIcon('location')}
                  </div>
                </TableCell>
                <TableCell className="sortableHeader" sx={{ width: userColumnWidths.actions, fontWeight: 600, whiteSpace: 'nowrap' }}>
                  <div className="headerContent">
                    <strong>Actions</strong>
                  </div>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {((loadingUsers || loadingLocations) && !users.length) ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5 }}>
                      <CircularProgress size={18} thickness={4} />
                      <Typography color="text.secondary">Loading...</Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : users.length > 0 ? (
                users.map((user) => {
                  const brightHrEmployeeId = user?.bright_hr_employee_id;
                  const currentLocationId = draftLocations[brightHrEmployeeId] ?? user?.location_id ?? '';
                  const isSaving = Boolean(savingLocations[brightHrEmployeeId]);
                  const statusLabel = user?.active ? 'Active' : 'Inactive';
                  const saveDisabled = isSaving || !hasLocationChanged(user);

                  return (
                    <TableRow key={brightHrEmployeeId} hover>
                      <TableCell sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user?.full_name || '-'}</TableCell>
                      <TableCell sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user?.job_title || '-'}</TableCell>
                      <TableCell sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user?.email || '-'}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{user?.payroll_number || '-'}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatJoiningDate(user?.employment_start)}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{statusLabel}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{user?.is_labour ? 'Yes' : 'No'}</TableCell>
                      <TableCell sx={{ width: userColumnWidths.location, maxWidth: userColumnWidths.location, overflow: 'hidden' }}>
                        <FormControl fullWidth size="small">
                          <Select
                            value={currentLocationId}
                            onChange={(event) => handleLocationChange(brightHrEmployeeId, event.target.value)}
                            displayEmpty
                            renderValue={(selected) => {
                              const selectedLocation = locationOptions.find((location) => location.location_id === selected);
                              return selectedLocation?.name || 'Unassigned';
                            }}
                          >
                            {locationOptions.map((location) => (
                              <MenuItem key={location.location_id || 'unassigned'} value={location.location_id}>
                                {location.name || location.location_id || 'Unassigned'}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handleSaveLocation(user)}
                          disabled={saveDisabled}
                        >
                          {isSaving ? 'Saving…' : 'Save'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                    No users found for the selected filter.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5, flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
            <TextField
              value={pageInput}
              onChange={handlePageInputChange}
              onKeyDown={handlePageInputKeyDown}
              label="Page"
              size="small"
              sx={{ width: 90 }}
              inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
              disabled={loadingUsers || !totalUsers}
            />
            <Typography variant="body2" color="text.secondary">
              {totalUsers ? `of ${Math.max(Math.ceil(totalUsers / rowsPerPage), 1)}` : ''}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {totalUsers ? `Showing ${page * rowsPerPage + 1}-${Math.min((page + 1) * rowsPerPage, totalUsers)} of ${totalUsers}` : 'No records to show'}
            </Typography>
          </Box>
          <TablePagination
            component="div"
            count={totalUsers}
            page={page}
            onPageChange={handlePageChange}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[rowsPerPage]}
            showFirstButton
            showLastButton
            labelRowsPerPage=""
            labelDisplayedRows={() => ''}
            sx={{
              '.MuiTablePagination-toolbar': { p: 0 },
              '.MuiTablePagination-selectLabel': { display: 'none' },
              '.MuiTablePagination-displayedRows': { m: 0 },
            }}
          />
        </Box>
      </Paper>
    </Box>
  );
};

export default ManageUsersPage;
